import React from 'react';
import { useState, useEffect } from 'react';
import { 
  Search, Filter, Globe, Code, Palette, BookOpen, Music, 
  Camera, Heart, Briefcase, Zap, Star, TrendingUp, Users,
  Play, Clock, Award, Target, Compass, MapPin, Lightbulb,
  ChevronRight, ChevronDown, Eye, Bookmark, Share2,
  Brain, Cpu, Database, Feather, Mic, Video, Headphones,
  Languages, Calculator, Microscope, Atom, Dna, Beaker,
  Mountain, Plane, Car, Utensils, Gamepad2, Dumbbell,
  PenTool, Scissors, Hammer, Wrench, Rocket, Smartphone
} from 'lucide-react';

const DiscoveryLearning = ({ darkMode, setDarkMode }) => {

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const categories = [
    { id: 'all', name: 'All Categories', icon: Compass, color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
    { id: 'languages', name: 'Languages', icon: Languages, color: 'bg-gradient-to-r from-blue-500 to-cyan-500' },
    { id: 'programming', name: 'Programming', icon: Code, color: 'bg-gradient-to-r from-green-500 to-emerald-500' },
    { id: 'creative', name: 'Creative Arts', icon: Palette, color: 'bg-gradient-to-r from-orange-500 to-red-500' },
    { id: 'science', name: 'Science', icon: Atom, color: 'bg-gradient-to-r from-indigo-500 to-purple-500' },
    { id: 'business', name: 'Business', icon: Briefcase, color: 'bg-gradient-to-r from-yellow-500 to-orange-500' },
    { id: 'lifestyle', name: 'Lifestyle', icon: Heart, color: 'bg-gradient-to-r from-pink-500 to-rose-500' },
    { id: 'tech', name: 'Technology', icon: Cpu, color: 'bg-gradient-to-r from-gray-500 to-gray-700' }
  ];

  const discoveryContent = [
    {
      id: 1,
      title: 'Learn Japanese Through Anime',
      category: 'languages',
      type: 'Interactive Course',
      difficulty: 'Beginner',
      duration: '3 months',
      rating: 4.8,
      students: '50K+',
      image: 'https://images.unsplash.com/photo-1528164344705-47542687000d?w=400&h=200&fit=crop',
      tags: ['Japanese', 'Culture', 'Entertainment', 'Conversation'],
      description: 'Master Japanese fundamentals using popular anime shows and cultural context.',
      trending: true,
      new: false,
      aiRecommended: true
    },
    {
      id: 2,
      title: 'Python for Data Science',
      category: 'programming',
      type: 'Project-Based',
      difficulty: 'Intermediate',
      duration: '6 weeks',
      rating: 4.9,
      students: '120K+',
      image: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400&h=200&fit=crop',
      tags: ['Python', 'Data Analysis', 'Machine Learning', 'Visualization'],
      description: 'Build real-world data science projects while learning Python programming.',
      trending: true,
      new: false,
      aiRecommended: false
    },
    {
      id: 3,
      title: 'Digital Art Fundamentals',
      category: 'creative',
      type: 'Studio Course',
      difficulty: 'Beginner',
      duration: '8 weeks',
      rating: 4.7,
      students: '25K+',
      image: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=200&fit=crop',
      tags: ['Digital Art', 'Design', 'Creative', 'Software'],
      description: 'Create stunning digital artwork using industry-standard tools and techniques.',
      trending: false,
      new: true,
      aiRecommended: true
    },
    {
      id: 4,
      title: 'Quantum Computing Basics',
      category: 'science',
      type: 'Theory + Practice',
      difficulty: 'Advanced',
      duration: '10 weeks',
      rating: 4.6,
      students: '8K+',
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=200&fit=crop',
      tags: ['Quantum', 'Physics', 'Computing', 'Future Tech'],
      description: 'Explore the fascinating world of quantum computing and its applications.',
      trending: true,
      new: true,
      aiRecommended: false
    },
    {
      id: 5,
      title: 'Startup Entrepreneurship',
      category: 'business',
      type: 'Case Studies',
      difficulty: 'Intermediate',
      duration: '4 weeks',
      rating: 4.5,
      students: '35K+',
      image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=200&fit=crop',
      tags: ['Entrepreneurship', 'Business', 'Startup', 'Innovation'],
      description: 'Learn from successful entrepreneurs and build your own startup strategy.',
      trending: false,
      new: false,
      aiRecommended: true
    },
    {
      id: 6,
      title: 'Mindfulness & Meditation',
      category: 'lifestyle',
      type: 'Guided Practice',
      difficulty: 'Beginner',
      duration: '30 days',
      rating: 4.9,
      students: '100K+',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop',
      tags: ['Mindfulness', 'Wellness', 'Mental Health', 'Stress Relief'],
      description: 'Develop inner peace and reduce stress through guided meditation practices.',
      trending: false,
      new: false,
      aiRecommended: false
    },
    {
      id: 7,
      title: 'Blockchain Development',
      category: 'tech',
      type: 'Hands-on',
      difficulty: 'Advanced',
      duration: '12 weeks',
      rating: 4.7,
      students: '15K+',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=200&fit=crop',
      tags: ['Blockchain', 'Cryptocurrency', 'Web3', 'Smart Contracts'],
      description: 'Build decentralized applications and smart contracts on various blockchains.',
      trending: true,
      new: true,
      aiRecommended: true
    },
    {
      id: 8,
      title: 'Spanish Conversation Mastery',
      category: 'languages',
      type: 'Live Sessions',
      difficulty: 'Intermediate',
      duration: '8 weeks',
      rating: 4.8,
      students: '40K+',
      image: 'https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?w=400&h=200&fit=crop',
      tags: ['Spanish', 'Conversation', 'Culture', 'Travel'],
      description: 'Achieve fluency through immersive conversation practice with native speakers.',
      trending: false,
      new: false,
      aiRecommended: true
    }
  ];

  const trendingTopics = [
    { name: 'AI & Machine Learning', growth: '+125%', icon: Brain },
    { name: 'Sustainable Living', growth: '+89%', icon: Heart },
    { name: 'Cryptocurrency', growth: '+76%', icon: Zap },
    { name: 'Remote Work Skills', growth: '+65%', icon: Briefcase },
    { name: 'Mental Health', growth: '+54%', icon: Target }
  ];

  const personalizedSuggestions = [
    {
      title: 'Complete Your Learning Path',
      subtitle: 'JavaScript Fundamentals → React Development',
      progress: 67,
      nextStep: 'Advanced React Patterns',
      color: 'from-blue-500 to-purple-500'
    },
    {
      title: 'Explore Similar Interests',
      subtitle: 'Since you enjoyed Python, try Data Science',
      progress: 0,
      nextStep: 'Start Discovery',
      color: 'from-green-500 to-teal-500'
    },
    {
      title: 'Trending in Your Field',
      subtitle: 'Web Development → WebAssembly',
      progress: 0,
      nextStep: 'Begin Learning',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const learningPaths = [
    {
      title: 'Full-Stack Developer',
      steps: ['HTML/CSS', 'JavaScript', 'React', 'Node.js', 'Database'],
      duration: '6 months',
      difficulty: 'Beginner to Advanced',
      popularity: 95
    },
    {
      title: 'Data Scientist',
      steps: ['Python', 'Statistics', 'ML', 'Deep Learning', 'Big Data'],
      duration: '8 months',
      difficulty: 'Intermediate to Advanced',
      popularity: 88
    },
    {
      title: 'UI/UX Designer',
      steps: ['Design Principles', 'Figma', 'User Research', 'Prototyping', 'Testing'],
      duration: '4 months',
      difficulty: 'Beginner to Intermediate',
      popularity: 82
    }
  ];

  const filteredContent = discoveryContent.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => item.tags.includes(tag));
    return matchesCategory && matchesSearch && matchesTags;
  });

  const allTags = [...new Set(discoveryContent.flatMap(item => item.tags))];

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Beginner': return 'text-green-500 bg-green-100';
      case 'Intermediate': return 'text-yellow-500 bg-yellow-100';
      case 'Advanced': return 'text-red-500 bg-red-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-light mb-4 bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
            Discover Your Next Learning Adventure
          </h1>
          <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Explore new skills, languages, and interests tailored to your learning journey
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search topics, skills, languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 rounded-2xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'} focus:ring-2 focus:ring-purple-500 transition-all`}
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-2xl flex items-center space-x-2 ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} transition-colors`}
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>            
           
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-6`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Difficulty</label>
                  <select className={`w-full p-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <option>All Levels</option>
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Duration</label>
                  <select className={`w-full p-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <option>Any Duration</option>
                    <option>Under 1 month</option>
                    <option>1-3 months</option>
                    <option>3+ months</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <select className={`w-full p-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <option>All Types</option>
                    <option>Interactive Course</option>
                    <option>Project-Based</option>
                    <option>Live Sessions</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {allTags.slice(0, 12).map(tag => (
                    <button
                      key={tag}
                      onClick={() => {
                        if (selectedTags.includes(tag)) {
                          setSelectedTags(selectedTags.filter(t => t !== tag));
                        } else {
                          setSelectedTags([...selectedTags, tag]);
                        }
                      }}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        selectedTags.includes(tag)
                          ? 'bg-purple-500 text-white'
                          : darkMode 
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="mb-8">
          <h2 className="text-2xl font-light mb-4">Explore Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-4 rounded-2xl transition-all duration-300 ${
                  selectedCategory === category.id
                    ? `${category.color} text-white shadow-lg scale-105`
                    : darkMode
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      : 'bg-white hover:bg-gray-100 text-gray-700'
                } border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <category.icon className="w-6 h-6 mx-auto mb-2" />
                <div className="text-xs font-medium text-center">{category.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Trending Topics */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-8`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-light">Trending Now</h2>
            <TrendingUp className="w-6 h-6 text-green-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {trendingTopics.map((topic, index) => (
              <div key={index} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors cursor-pointer`}>
                <topic.icon className="w-6 h-6 text-purple-500 mb-2" />
                <div className="font-medium text-sm mb-1">{topic.name}</div>
                <div className="text-xs text-green-500 font-medium">{topic.growth}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Personalized Suggestions */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-8`}>
          <div className="flex items-center space-x-2 mb-6">
            <Lightbulb className="w-6 h-6 text-yellow-500" />
            <h2 className="text-2xl font-light">Personalized for You</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personalizedSuggestions.map((suggestion, index) => (
              <div key={index} className={`p-6 rounded-xl bg-gradient-to-r ${suggestion.color} text-white`}>
                <h3 className="font-medium mb-2">{suggestion.title}</h3>
                <p className="text-sm opacity-90 mb-4">{suggestion.subtitle}</p>
                {suggestion.progress > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Progress</span>
                      <span>{suggestion.progress}%</span>
                    </div>
                    <div className="w-full bg-white bg-opacity-30 rounded-full h-2">
                      <div 
                        className="bg-white h-2 rounded-full transition-all duration-300"
                        style={{ width: `${suggestion.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                <button className="w-full py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-colors">
                  {suggestion.nextStep}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Learning Paths */}
        <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-8`}>
          <h2 className="text-2xl font-light mb-6">Structured Learning Paths</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {learningPaths.map((path, index) => (
              <div key={index} className={`p-6 rounded-xl border ${darkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'} transition-colors cursor-pointer`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">{path.title}</h3>
                  <div className="flex items-center space-x-1 text-sm text-yellow-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span>{path.popularity}%</span>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  {path.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex items-center space-x-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${stepIndex === 0 ? 'bg-purple-500 text-white' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                        {stepIndex + 1}
                      </div>
                      <span className="text-sm">{step}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{path.duration}</span>
                  </div>
                  <span>{path.difficulty}</span>
                </div>
                
                <button className={`w-full py-2 rounded-lg ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-500 hover:bg-purple-600'} text-white text-sm font-medium transition-colors`}>
                  Start Path
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Discovery Content Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-light">
              Discover Content 
              <span className={`ml-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                ({filteredContent.length} results)
              </span>
            </h2>
            <div className="flex items-center space-x-2">
              <button className={`px-3 py-1 text-sm rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                Most Popular
              </button>
              <button className={`px-3 py-1 text-sm rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                Newest
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredContent.map(item => (
              <div key={item.id} className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group`}>
                <div className="relative">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3 flex space-x-2">
                    {item.trending && (
                      <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                        🔥 Trending
                      </span>
                    )}
                    {item.new && (
                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                        ✨ New
                      </span>
                    )}
                    {item.aiRecommended && (
                      <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full">
                        🤖 AI Pick
                      </span>
                    )}
                  </div>
                  <div className="absolute top-3 right-3">
                    <button className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-colors">
                      <Bookmark className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(item.difficulty)} ${darkMode ? 'bg-opacity-20' : ''}`}>
                      {item.difficulty}
                    </span>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <Star className="w-3 h-3 text-yellow-500 fill-current" />
                      <span>{item.rating}</span>
                    </div>
                  </div>
                  
                  <h3 className="font-medium mb-2 line-clamp-2">{item.title}</h3>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2`}>
                    {item.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{item.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="w-3 h-3" />
                      <span>{item.students}</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {item.tags.slice(0, 3).map(tag => (
                      <span key={tag} className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex space-x-2">
                    <button className={`flex-1 py-2 rounded-lg ${darkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-500 hover:bg-purple-600'} text-white text-sm font-medium transition-colors flex items-center justify-center space-x-2`}>
                      <Play className="w-4 h-4" />
                      <span>Start Learning</span>
                    </button>
                    <button className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}>
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryLearning;